import {
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import type { useSavedQueries } from '../../../../../hooks/QueryBuilder/useSavedQueries';
import { isAdvancedQuerySource } from '../../../../../utils/queryBuilderUtils';

type SavedQuery = ReturnType<typeof useSavedQueries>['queries'][number];

interface SavedQueriesTableProps {
  queries: SavedQuery[];
  selectedQueryId: number | null;
  selectingId: string | null;
  onPick: (query: { queryId: string; description: string }) => void;
}

const COLUMNS = [
  { label: 'Description', width: '35%' },
  { label: 'Groupe', width: '15%' },
  { label: 'Date', width: '15%' },
  { label: 'Type', width: '15%' },
  { label: 'Action', width: '20%', align: 'center' as const },
];

const queryLabel = (query: SavedQuery) => query.DESCRIPTION || query.QUERY_NAME || '';

function SavedQueriesTable({ queries, selectedQueryId, selectingId, onPick }: SavedQueriesTableProps) {
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          {COLUMNS.map(column => <col key={column.label} style={{ width: column.width }} />)}
        </colgroup>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            {COLUMNS.map(column => (
              <TableCell key={column.label} sx={{ fontWeight: 600 }} align={column.align}>{column.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {queries.map(query => {
            const isSelected = selectedQueryId !== null && String(selectedQueryId) === query.QUERY_ID;
            const isSelecting = selectingId === query.QUERY_ID;
            const isAdvanced = isAdvancedQuerySource(query.SOURCE);
            return (
              <TableRow key={query.QUERY_ID} hover selected={isSelected}>
                <TableCell sx={{ overflow: 'hidden' }}>
                  <Typography variant="body2" noWrap title={queryLabel(query)}>
                    {queryLabel(query) || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {query.QUERY_GROUP_NAME && (
                    <Chip label={query.QUERY_GROUP_NAME} size="small" sx={{ bgcolor: 'badge.blue.bg', color: 'badge.blue.fg' }} />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(query.APPLICATION_DATE).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={isAdvanced ? 'Avancé' : 'Simple'}
                    size="small"
                    sx={isAdvanced ? { bgcolor: 'badge.orange.bg', color: 'badge.orange.fg' } : { bgcolor: 'badge.green.bg', color: 'badge.green.fg' }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant={isSelected ? 'contained' : 'outlined'}
                    color={isSelected ? 'success' : 'primary'}
                    startIcon={isSelected ? <CheckCircleOutlineIcon fontSize="small" /> : undefined}
                    disabled={isSelecting}
                    onClick={() => onPick({ queryId: query.QUERY_ID, description: queryLabel(query) })}
                    sx={{ textTransform: 'none' }}
                  >
                    {isSelecting ? <CircularProgress size={16} /> : isSelected ? 'Sélectionnée' : 'Sélectionner'}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default SavedQueriesTable;
